import { ButtonGroup, Button, Badge } from '@superops/ui-design-system';
import {
  EventEmitter,
  DeleteModal,
  getTranslatedText,
  toast,
  sendMessageToContentJS,
  useActivityTracker,
  useProduct,
  EventEmitterWithIndex
} from '@nile/components';
import Tippy from '@tippyjs/react';
import {
  DELETE_WORKLOG_TIMER,
  UPDATE_WORKLOG_TIMER_ENTRY
} from '@nile/graphql';
import React, { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import { Tooltip } from 'react-clone-tippy';
import WorklogHoursSection from '@nile/components/src/Containers/NewWorklog/WorklogHoursSection';
import Timer from './Timer';
import AssociateWorkItem from './AssociateWorkItem';
import { pick } from 'lodash';
import { removePropertiesForItProduct } from '@nile/itsm';
import WorklogTask from './WorklogTask';

const TimerEntry = styled.div`
  padding: ${(props) => (props.manual ? '24px 24px 8px 24px' : '8px 24px')};
  background: ${(props) =>
    props?.workItem
      ? 'var(--sops-white-to-grey-bg)'
      : 'var(--sops-timer-workitem-bg)'};
  transition: opacity 200ms linear;

  .Superops-Button {
    box-shadow: none !important;
  }
  .timer-type {
    left: 2px;
    top: 2px;
    font-size: 10px;
    padding-top: 2px;
    line-height: 15px;
    border-radius: 5px;
    background: #f6ddc6;
    padding-bottom: 2px;
  }
  .timer-cell {
    margin-left: auto;
  }
  .details {
    width: ${(props) => `calc(100% - ${props?.workItem ? '222px' : '270px'})`};
  }
  .faded {
    color: #3a3242;
  }

  .action-button {
    opacity: 0;
  }

  &:hover {
    background: #f5f5fc;

    .action-button {
      opacity: 1;
    }
    .timer-cell {
      background: #ffffff;
    }
  }
`;

const WorklogEditPopupContainer = styled.div`
  min-width: 500px;
`;

function TimerList(props) {
  const {
    intl,
    timer,
    refetchEntries,
    tabContext,
    associateWorkItem,
    handleParentClose,
    editWorklogZIndex,
    taskViewOnly
  } = props;

  const [
    updateWorklogTimerEntryWithInterceptor,
    {
      loading: updateWorklogTimerEntryLoading,
      data: updatedWorklogTimerEntry,
      error: updateWorklogTimerEntryError
    }
  ] = useMutation(UPDATE_WORKLOG_TIMER_ENTRY, {
    onCompleted({ updateWorklogTimerEntry: currentCreatedTimer }) {
      if (updateWorklogTimerEntry) {
        refetchEntries();
        EventEmitter.dispatch('refetchTimerEntries');
        EventEmitterWithIndex.dispatch('refetchTimerEntries');
        sendMessageToContentJS('refetchGlobalTimers');
        // EventEmitter.dispatch('refetchGlobalTimers');
        // toast('success', 'timer.entry.update.success.toast');
      }
    }
  });
  const currentProduct = useProduct();

  async function updateWorklogTimerEntry(variables) {
    try {
      const updatedPayload = removePropertiesForItProduct(
        variables,
        'updateWorklogTimerEntry',
        currentProduct
      );
      return await updateWorklogTimerEntryWithInterceptor(updatedPayload);
    } catch (ex) {
      return ex;
    }
  }

  const [
    deleteWorklogTimerEntry,
    {
      loading: deleteWorklogTimerEntryLoading,
      data: deletedEntry,
      error: deleteWorklogTimerEntryError
    }
  ] = useMutation(DELETE_WORKLOG_TIMER, {
    onCompleted(res) {
      if (res?.deleteWorklogTimerEntry) {
        refetchEntries();
        EventEmitter.dispatch('refetchTimerEntries');
        EventEmitter.dispatch('refetchGlobalTimers');
        sendMessageToContentJS('refetchGlobalTimers');

        toast('success', 'timer.entry.delete.success.toast');
      }
    }
  });

  const getName = (timer) => {
    switch (timer?.workItem?.module) {
      case 'TICKET':
        return 'TicketFilled';
      case 'PROJECT':
        return 'ProjectFilled';
      default:
        return 'AssociateEntity';
    }
  };

  const handleAssociateWorkItem = (module, workItem) => {
    useActivityTracker({
      event:
        module === 'TICKET' ? 'Associated to ticket' : 'Associated to project'
    });
    return updateWorklogTimerEntry({
      variables: {
        timerEntryInput: {
          timerId: timer?.timerId,
          workItem: { ...pick(workItem, ['workId']), module: module }
        }
      }
    });
  };

  const navigateToModule = (timer) => {
    switch (timer?.workItem?.module) {
      case 'PROJECT':
        tabContext.current.openTabCb({
          projectId: timer.workItem?.workId,
          module: 'PROJECT_DETAIL',
          data: {
            projectId: timer.workItem?.workId,
            title: timer.workItem?.title
          }
        });
        break;
      default:
        tabContext.current.openTabCb({
          ticketId: timer.workItem.workId,
          module: 'TICKET',
          data: {
            subject: timer.workItem?.subject,
            //   displayId: timer.ticket?.displayId,
            ticketId: timer.workItem?.workId
          }
        });
        break;
    }
    handleParentClose();
  };

  const associatedTask = timer?.task;
  const getSubject = (timer) => {
    let Subject = 'timer.unknown.no.ticket.project.associated';
    if (timer?.workItem) {
      Subject = timer?.workItem?.subject || timer?.workItem?.title;
    }

    return (
      <div
        className={`f-14 pointer subject ellipsis ${
          timer?.workItem ? 'text-medium' : 'faded'
        }`}
        onClick={timer?.workItem && navigateToModule.bind(this, timer)}
      >
        <FormattedMessage id={Subject} />
      </div>
    );
  };

  const closeAllOpenPopups = () => {
    ['EditTimerParent', 'AssociateWorkItemParent'].forEach((pName) => {
      if (
        document.getElementsByClassName(pName) &&
        document.getElementsByClassName(pName)?.length
      ) {
        Array.prototype.forEach.call(
          document.getElementsByClassName(pName),
          (popup) => {
            if (popup?._tippy?.state?.isVisible) {
              popup?._tippy.hide();
            }
          }
        );
      }
    });
  };

  const autoTimer = timer?.type === 'AUTOMATIC';

  return (
    <TimerEntry
      key={timer?.itemId}
      workItem={timer?.workItem}
      // manual={!autoTimer}
      className={`flex g10 center fx-sb position-relative ${
        updateWorklogTimerEntryLoading ? 'disabled' : ''
      } ${props?.parentClassName}`}
    >
      {/* {timer?.type && !autoTimer ? (
        <span className="timer-type pr5 pl5 br10 capitalize position-absolute">
          {timer?.type}
        </span>
      ) : null} */}
      <div className="flex center">
        <SVGIcon name={getName(timer)} />
      </div>
      <div className="fx fcol details">
        {getSubject(timer)}

        {timer.notes ? (
          <span className="f-12" style={{ color: '#0B0114' }}>
            {timer?.notes}
          </span>
        ) : (
          <Tooltip
            title={getTranslatedText(intl, 'timer.add.note.to.convert')}
            position="top-start"
            inertia
            className="full-width"
            trigger="mouseenter"
            animation="shift"
            animateFill={false}
          >
            <Badge
              size="large"
              icon={<SVGIcon name="WarningOutline" size="sm" />}
              iconPosition="left"
              status="attention"
            >
              <FormattedMessage id="workItem.details.add.note" />
            </Badge>
          </Tooltip>
        )}

        {associatedTask && (
          <div className="f-10 ellipsis">
            <WorklogTask
              task={timer?.task}
              viewOnly
              iconSize="xs"
              classNames="fsize12"
              gap="g5"
            />
          </div>
        )}
      </div>

      {!timer?.workItem && (
        <ButtonGroup segmented>
          <DeleteModal
            modalActivator={
              <Button
                icon={
                  <div id="delete-modal">
                    <SVGIcon name="Delete" color="#EC9E9E" />
                  </div>
                }
              />
            }
            title={getTranslatedText(intl, 'timer.unknown.delete.title')}
            handleDeleteConfirmation={() => {
              deleteWorklogTimerEntry({
                variables: {
                  deleteWorklogTimerInput: {
                    timerId: timer?.timerId
                  }
                }
              });
            }}
            // handleDeleteConfirmation={handleDelete}
            description={getTranslatedText(
              intl,
              'timer.unknown.delete.description'
            )}
          />
          <AssociateWorkItem
            noPrefix
            timer={timer}
            handleAssociateWorkItem={handleAssociateWorkItem}
            closeAllOpenPopups={closeAllOpenPopups}
            handleParentClose={handleParentClose}
            triggerElement={
              <Tooltip
                title={getTranslatedText(intl, 'timer.associate.to.workItem')}
                delay={400}
                duration={250}
                position="top-start"
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
              >
                <Button
                  icon={<SVGIcon name="RightArrowCircle" color="#4B8413" />}
                />
              </Tooltip>
            }
          />
        </ButtonGroup>
      )}
      <div className="action-button mlauto">
        <EditTimer
          timer={timer}
          autoTimer={autoTimer}
          associateWorkItem={associateWorkItem}
          handleAssociateWorkItem={handleAssociateWorkItem}
          handleParentClose={handleParentClose}
          closeAllOpenPopups={closeAllOpenPopups}
          editWorklogZIndex={editWorklogZIndex}
          taskViewOnly={taskViewOnly}
        />
      </div>
      <Timer
        timer={timer}
        updateWorklogTimerEntry={updateWorklogTimerEntry}
        updateWorklogTimerEntryLoading={updateWorklogTimerEntryLoading}
      />
    </TimerEntry>
  );
}

export const EditTimer = (props) => {
  const {
    timer,
    autoTimer,
    leftInline,
    associateWorkItem,
    handleAssociateWorkItem,
    handleParentClose,
    handleOutsideClick,
    hideWorklogTask,
    editWorklogZIndex,
    taskViewOnly,
    hideTaskWorkItem,
    hideBlockItemConsumptionInfo
  } = props;
  const popupParentName = 'EditTimerParent';
  const editTippyRef = useRef();
  let timeout = null;
  const [editMounted, setEditMounted] = useState(false);

  const closeAllOpenPopups = () => {
    if (
      document.getElementsByClassName(popupParentName) &&
      document.getElementsByClassName(popupParentName)?.length
    ) {
      Array.prototype.forEach.call(
        document.getElementsByClassName(popupParentName),
        (popup) => {
          if (popup?._tippy.state.isVisible) {
            popup?._tippy.hide();
          }
        }
      );
    }
  };

  function closePopover() {
    setTimeout(() => {
      setEditMounted(false);
      editTippyRef.current._tippy.hide();
      // document.removeEventListener('click', handleClickOutside);
    }, 50);
  }

  function handleClickOutside(e) {
    if (handleOutsideClick) {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        const clickedOutside = [];
        ['EditTimerParent', 'AssociateWorkItemParent'].forEach((pName) => {
          document.getElementsByClassName(pName).forEach((popup) => {
            if (
              popup?._tippy?.state?.isVisible &&
              popup._tippy.popper.contains(e.target)
            ) {
              clickedOutside.push(true);
            }
          });
        });
        if (!clickedOutside.some((res) => res)) {
          closePopover();
        }
      }, 20);
    }
  }

  const toggleEditPopover = () => {
    if (editTippyRef?.current?._tippy) {
      if (!editTippyRef.current._tippy.state?.isVisible) {
        setEditMounted(true);
        if (props?.closeAllOpenPopups) {
          props?.closeAllOpenPopups();
        } else {
          closeAllOpenPopups();
        }
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          editTippyRef.current._tippy.popperInstance.forceUpdate();
        }, 2000);
        // document.addEventListener('click', handleClickOutside);
        editTippyRef.current._tippy.show();
      } else if (editTippyRef.current._tippy.state?.isVisible) {
        closePopover();
      }
    }
  };

  function repositionPopup() {
    if (editTippyRef.current._tippy) {
      editTippyRef.current._tippy.popperInstance.forceUpdate();
    }
  }

  return (
    <Tippy
      content={
        editMounted ? (
          <WorklogEditPopupContainer
            onChange={repositionPopup}
            key={timer?.timerId}
          >
            <WorklogHoursSection
              editMode
              timer={timer}
              onlyManual={!autoTimer}
              associateWorkItem={associateWorkItem}
              handleAssociateWorkItem={handleAssociateWorkItem}
              workItem={timer?.workItem}
              handleClose={closePopover}
              handleParentClose={handleParentClose}
              module={timer?.module || null}
              repositionPopup={repositionPopup}
              disableAutoTimerCloseOnSuccess
              hideWorklogTask={hideWorklogTask}
              taskViewOnly={taskViewOnly}
              hideTaskWorkItem={hideTaskWorkItem}
              hideBlockItemConsumptionInfo={hideBlockItemConsumptionInfo}
            />
          </WorklogEditPopupContainer>
        ) : null
      }
      trigger="manual" // manually close the tip on api calls
      interactive
      placement="right"
      arrow={false}
      theme="light"
      allowHTML
      ref={(node) => {
        editTippyRef.current = node;
      }}
      appendTo={document.body}
      offset={[-150, 30]}
      flipOnUpdate
      // onShow={() => {
      //   setEditMounted(true);
      // }}
      // onHide={() => {
      //   setEditMounted(false);
      // }}
      hideOnClick="toggle"
      maxWidth={500}
      zIndex={editWorklogZIndex || 520}
    >
      <div
        key={timer?.itemId}
        className={`${
          leftInline ? '' : 'mlauto'
        } ptr position-relative ${popupParentName}`}
        onClick={toggleEditPopover}
      >
        <SVGIcon name="Edit" />
      </div>
    </Tippy>
  );
};

export default TimerList;
